<template>
  <div class="about-page">
    <!-- Hero Section -->
    <Hero
      :title="$t('aboutPage.hero.title')"
      :subtitle="$t('aboutPage.hero.subtitle')"
      :carouselEnabled="true"
      :slides="heroSlides"
      :button1="null"
      :button2="null"
    />

    <!-- Team Section -->
    <section class="team-section">
      <div class="container">
        <div class="team-grid">
          <div v-for="(member, index) in teamMembers" :key="index" class="team-card">
            <div class="card-photo-wrap">
              <img v-if="member.photo" :src="member.photo" :alt="member.name" class="card-photo" />
              <div v-else class="card-photo-fallback">
                <span>{{
                  member.name
                    .split(' ')
                    .map(n => n[0])
                    .join('')
                }}</span>
              </div>
            </div>

            <div class="card-body">
              <h3 class="card-name">{{ member.name }}</h3>
              <span class="card-role">{{ member.role }}</span>
              <p class="card-bio">{{ member.bio }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Final CTA -->
    <div class="final-cta-wrapper">
      <FinalCTA />
    </div>
  </div>
</template>

<script>
import Hero from '../sections/Hero.vue';
import FinalCTA from '../sections/FinalCTA.vue';

export default {
  name: 'About',
  components: {
    Hero,
    FinalCTA
  },
  data() {
    return {
      heroSlides: [this.$t('aboutPage.hero.slideImage')],
      teamMembers: [
        {
          name: 'Anthony Cotter',
          role: 'Founder',
          photo: 'https://dashboard.wholelifegoals.com/wp-content/uploads/2021/11/anthony-whole-life-goals.jpg',
          bio: this.$t('aboutPage.team.anthony.bio')
        },
        {
          name: 'David Schiller',
          role: 'CFO',
          photo: 'https://dashboard.wholelifegoals.com/wp-content/uploads/2021/06/David-Schiller-1-1.png',
          bio: this.$t('aboutPage.team.david.bio')
        },
        {
          name: 'Sloan Sehr',
          role: 'CMO',
          photo: 'https://dashboard.wholelifegoals.com/wp-content/uploads/2021/06/Sloan-Headshot.jpeg',
          bio: this.$t('aboutPage.team.sloan.bio')
        },
        {
          name: 'Joseph de Sena',
          role: 'Strategic Advisor',
          photo: 'https://dashboard.wholelifegoals.com/wp-content/uploads/2021/06/Joe-de-Sena.png',
          bio: this.$t('aboutPage.team.joseph.bio')
        },
        {
          name: 'Alexandra Kazimir',
          role: 'Project Manager',
          photo: 'https://dashboard.wholelifegoals.com/wp-content/uploads/2019/10/ALXANDRA.jpg',
          bio: this.$t('aboutPage.team.alexandra.bio')
        },
        {
          name: 'Liam Tracy',
          role: 'CTO',
          photo: 'https://dashboard.wholelifegoals.com/wp-content/uploads/2019/10/LIAM.jpg',
          bio: this.$t('aboutPage.team.liam.bio')
        },
        {
          name: 'Orion Bayo',
          role: 'Chief Engineer',
          photo: 'https://dashboard.wholelifegoals.com/wp-content/uploads/2019/10/ORION.jpg',
          bio: this.$t('aboutPage.team.orion.bio')
        },
        {
          name: 'Rudi Bach',
          role: 'COO',
          photo: 'https://dashboard.wholelifegoals.com/wp-content/uploads/2019/10/RUDI.jpg',
          bio: this.$t('aboutPage.team.rudi.bio')
        },
        {
          name: 'Steve Halstead',
          role: 'Director of Operations',
          photo: 'https://dashboard.wholelifegoals.com/wp-content/uploads/2019/10/STEVE.jpg',
          bio: this.$t('aboutPage.team.steve.bio')
        }
      ]
    };
  }
};
</script>

<style lang="scss" scoped>
@import '@/assets/_variables.scss';

// ─── Team ─────────────────────────────────────────────────────────────────────

.team-section {
  padding: 2.5rem 2rem 6rem;
  background: $bg-light;

  @media (max-width: 768px) {
    padding: 1.5rem 1.5rem 4rem;
  }
}

.section-title {
  font-size: 2.4rem;
  font-weight: 700;
  color: $text-dark;
  text-align: center;
  margin-bottom: 0.75rem;

  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
}

.team-subtitle {
  text-align: center;
  color: $text-light;
  font-size: 1.05rem;
  max-width: 560px;
  margin: -1rem auto 3.5rem;
  line-height: 1.6;
}

.team-grid {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.team-card {
  background: $bg-white;
  border-radius: 16px;
  border: 1px solid $border;
  overflow: hidden;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 1.5rem;
  gap: 1.5rem;
  transition:
    box-shadow 0.25s ease,
    transform 0.25s ease;

  &:hover {
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.09);
    transform: translateY(-3px);
  }

  @media (max-width: 560px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
}

.card-photo-wrap {
  width: 220px;
  min-width: 220px;
  height: 220px;
  border-radius: 50%;
  overflow: hidden;
  background: $bg-light;
  border: 3px solid rgba($brand-color, 0.2);
  flex-shrink: 0;

  @media (max-width: 560px) {
    width: 160px;
    min-width: 160px;
    height: 160px;
  }
}

.card-photo {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top center;
  display: block;
}

.card-photo-fallback {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba($brand-color, 0.07);

  span {
    font-size: 1.5rem;
    font-weight: 700;
    color: $brand-color;
    letter-spacing: 0.04em;
  }
}

.card-body {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  flex: 1;
  min-width: 0;
}

.card-name {
  font-size: 1.15rem;
  font-weight: 700;
  color: $text-dark;
  margin: 0 0 0.25rem;
}

.card-role {
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: $brand-color;
  background: rgba($brand-color, 0.08);
  padding: 0.25rem 0.65rem;
  border-radius: 100px;
  margin-bottom: 1rem;
  align-self: flex-start;

  @media (max-width: 560px) {
    align-self: center;
  }
}

.card-bio {
  font-size: 0.92rem;
  color: $text-light;
  line-height: 1.75;
  margin: 0;
}

// ─── Final CTA ────────────────────────────────────────────────────────────────

.final-cta-wrapper {
  margin-bottom: -5rem;
}
</style>
